import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { API } from "@/lib/api";

const SID_KEY = "jps_chat_sid";
const WELCOME = {
  role: "assistant",
  content:
    "Hi! I'm Pep, the Just Peptides assistant. Ask me about products, pricing, ordering, payments, or telehealth. (Research use only — I can't give medical or dosing advice.)",
};

const getSid = () => {
  let sid = localStorage.getItem(SID_KEY);
  if (!sid) {
    sid = (crypto.randomUUID && crypto.randomUUID()) || `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(SID_KEY, sid);
  }
  return sid;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = useRef(getSid());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";
        for (const chunk of chunks) {
          const line = chunk.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
              return copy;
            });
          } else if (payload.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: payload.error };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Sorry, I couldn't reach the assistant. Please email info@justpeptidessupply.com.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end">
      {open && (
        <div
          data-testid="chat-panel"
          className="mb-3 w-[92vw] max-w-[380px] h-[540px] max-h-[75vh] rounded-2xl bg-white shadow-2xl border border-brand-navy/10 flex flex-col overflow-hidden fade-up"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-brand-navy text-white">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-brand-teal" />
              <span className="font-semibold text-sm tracking-wide">Ask Pep · Just Peptides</span>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close" aria-label="Close chat" className="text-white/70 hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-brand-mist/40">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "bg-brand-blue text-white rounded-br-sm" : "bg-white text-brand-navy border border-brand-navy/10 rounded-bl-sm"
                  }`}
                >
                  {m.content || (busy && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-brand-navy/10 p-3 bg-white">
            <div className="flex items-end gap-2">
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder="Type your question…"
                className="flex-1 resize-none rounded-xl border border-brand-navy/15 px-3 py-2 text-sm outline-none focus:border-brand-blue max-h-28"
              />
              <button
                onClick={send}
                disabled={busy || !input.trim()}
                data-testid="chat-send"
                className="shrink-0 h-10 w-10 rounded-xl bg-brand-navy hover:bg-brand-blue disabled:opacity-40 text-white flex items-center justify-center transition-colors"
                aria-label="Send message"
              >
                <Send size={16} />
              </button>
            </div>
            <p className="mt-2 text-[10px] text-brand-navy/40 text-center">For research use only · not medical advice</p>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-toggle"
        aria-label="Open chat assistant"
        className="h-14 w-14 rounded-full bg-brand-navy hover:bg-brand-blue text-white shadow-xl flex items-center justify-center transition-colors"
      >
        {open ? <X size={22} /> : <MessageCircle size={24} />}
      </button>
    </div>
  );
};
